/**
 * Archive and delete-my-copy (ruling R-0828-1) — the two housekeeping writes.
 *
 * Its own module rather than a corner of `inbox.ts` for the same reason the
 * server put them under `/conversations`: they act on either side of a door. The
 * side is never sent — the server resolves it from the knock, so nothing here
 * can name whose copy is being written.
 */
import { call, post } from "./core";

export type ConversationSide = "incoming" | "outgoing";

export type ConversationMarkResult =
  | { ok: true; side: ConversationSide | null }
  | { ok: false; code: "UNAUTHENTICATED" | "KNOCK_NOT_FOUND" | "UNKNOWN" };

function sideOf(body: unknown): ConversationSide | null {
  const side = (body as Record<string, unknown>)["side"];
  return side === "incoming" || side === "outgoing" ? side : null;
}

function failure(status: number): ConversationMarkResult {
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (status === 404) return { ok: false, code: "KNOCK_NOT_FOUND" };
  return { ok: false, code: "UNKNOWN" };
}

/** Put a conversation away, or bring it back. One call, because it is one write. */
export async function setConversationArchived(input: {
  knock_id: string;
  archived: boolean;
}): Promise<ConversationMarkResult> {
  const r = await call(() =>
    post("/api/v1/conversations/archive", {
      knock_id: input.knock_id,
      archived: input.archived,
    }),
  );
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return failure(r.res.status);
  return { ok: true, side: sideOf(r.body) };
}

/**
 * Delete the reader's own copy. There is no undo and no `undo_token` to hold on
 * to: the caller's job is the confirm, and this is what happens after it.
 */
export async function deleteConversationCopy(input: {
  knock_id: string;
}): Promise<ConversationMarkResult> {
  const r = await call(() => post("/api/v1/conversations/delete", { knock_id: input.knock_id }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return failure(r.res.status);
  return { ok: true, side: sideOf(r.body) };
}
